import { scopeProjectRef } from "@t3tools/client-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChatHeaderDraftProjectPicker } from "../components/chat/ChatHeader";
import { type DraftId, type DraftSessionState, useComposerDraftStore } from "../composerDraftStore";
import { resolveDraftProjectRecovery } from "../draftProjectRecovery";
import { deriveLogicalProjectKey } from "../logicalProject";
import { selectEnvironmentState, useStore } from "../store";
import type { Project } from "../types";
import { useOrderedProjects } from "./useOrderedProjects";

interface UseDraftProjectSelectionInput {
  draftId: DraftId | null;
  draftSession: DraftSessionState | null;
  activeProject: Project | undefined;
}

interface UseDraftProjectSelectionResult {
  draftProjectPicker: ChatHeaderDraftProjectPicker | null;
  openProjectPicker: () => void;
}

/**
 * Owns the project chip state for a draft thread: silently repoints drafts whose
 * project can be recovered, and otherwise lets the user pick one from the header.
 */
export function useDraftProjectSelection(
  input: UseDraftProjectSelectionInput,
): UseDraftProjectSelectionResult {
  const { draftId, draftSession, activeProject } = input;
  const projects = useOrderedProjects();
  const reassignDraftSessionProject = useComposerDraftStore(
    (store) => store.reassignDraftSessionProject,
  );
  const environmentBootstrapComplete = useStore((store) =>
    draftSession
      ? selectEnvironmentState(store, draftSession.environmentId).bootstrapComplete
      : false,
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  const assignProject = useCallback(
    (project: Project) => {
      if (!draftId) {
        return;
      }
      reassignDraftSessionProject(
        draftId,
        scopeProjectRef(project.environmentId, project.id),
        deriveLogicalProjectKey(project),
      );
    },
    [draftId, reassignDraftSessionProject],
  );

  useEffect(() => {
    if (!draftId || !draftSession || activeProject || !environmentBootstrapComplete) {
      return;
    }
    const recovered = resolveDraftProjectRecovery({
      draftSession,
      environmentProjects: projects,
    });
    if (recovered) {
      assignProject(recovered);
    }
  }, [activeProject, assignProject, draftId, draftSession, environmentBootstrapComplete, projects]);

  const openProjectPicker = useCallback(() => {
    setPickerOpen(true);
  }, []);

  const onSelectProject = useCallback(
    (project: Project) => {
      assignProject(project);
      setPickerOpen(false);
    },
    [assignProject],
  );

  const showChip = draftId !== null && draftSession !== null && environmentBootstrapComplete;

  const draftProjectPicker = useMemo<ChatHeaderDraftProjectPicker | null>(
    () =>
      showChip
        ? {
            projects,
            activeProject,
            projectMissing: activeProject === undefined && draftSession.projectId !== null,
            open: pickerOpen,
            onOpenChange: setPickerOpen,
            onSelectProject,
          }
        : null,
    [activeProject, draftSession, onSelectProject, pickerOpen, projects, showChip],
  );

  return { draftProjectPicker, openProjectPicker };
}
